import React, { useMemo, useState } from 'react';

export type SessionStatus = 'Scheduled' | 'InProgress' | 'Completed' | 'Cancelled' | 'Rescheduled' | 'NoShow';
export type AttendanceStatus = 'Present' | 'Absent' | 'Late' | 'Excused' | 'NoShow';

export interface ScheduleSessionItem {
  id: number;
  scheduleId: number;
  organizationId: number;
  classroomId?: number | null;
  classroomName?: string;
  teacherUserId: number;
  teacherDisplayName?: string;
  studentUserId?: number | null;
  studentDisplayName?: string;
  targetType?: string;
  startDateTime: string;
  endDateTime: string;
  actualStartDateTime?: string | null;
  actualEndDateTime?: string | null;
  status: SessionStatus;
  isModified?: boolean;
  originalStartDateTime?: string | null;
  originalEndDateTime?: string | null;
  location?: string | null;
  meetingUrl?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateScheduleSessionRequest {
  startDateTime: string;
  endDateTime: string;
  status: SessionStatus;
  location: string | null;
  meetingUrl: string | null;
  notes: string | null;
}

export interface RecordAttendanceRequest {
  userId: number;
  status: AttendanceStatus;
  joinedAt?: string | null;
  leftAt?: string | null;
  notes?: string | null;
}

interface SessionEditFormState {
  startDateTime: string;
  endDateTime: string;
  status: SessionStatus;
  location: string;
  meetingUrl: string;
  notes: string;
}

interface ScheduleSessionsPanelProps {
  sessions: ScheduleSessionItem[];
  loading?: boolean;
  mode?: 'tab' | 'popup';
  onUpdateSession?: (sessionId: number, payload: UpdateScheduleSessionRequest) => Promise<void>;
  onStartSession?: (sessionId: number) => Promise<void>;
  onCompleteSession?: (sessionId: number) => Promise<void>;
  onRecordAttendance?: (sessionId: number, payload: RecordAttendanceRequest) => Promise<void>;
}

const SESSION_STATUS_OPTIONS: SessionStatus[] = ['Scheduled', 'InProgress', 'Completed', 'Cancelled', 'Rescheduled', 'NoShow'];
const ATTENDANCE_STATUS_OPTIONS: AttendanceStatus[] = ['Present', 'Absent', 'Late', 'Excused', 'NoShow'];

const toDateTimeInput = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const toDisplayDateTime = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const toDisplayDate = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString();
};

const toDisplayTime = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatSessionRange = (startValue?: string | null, endValue?: string | null): string => {
  if (!startValue || !endValue) {
    return `${toDisplayDateTime(startValue)} - ${toDisplayDateTime(endValue)}`;
  }

  const start = new Date(startValue);
  const end = new Date(endValue);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${toDisplayDateTime(startValue)} - ${toDisplayDateTime(endValue)}`;
  }

  const sameDate = start.getFullYear() === end.getFullYear()
    && start.getMonth() === end.getMonth()
    && start.getDate() === end.getDate();

  if (sameDate) {
    return `${toDisplayDate(startValue)} ${toDisplayTime(startValue)} - ${toDisplayTime(endValue)}`;
  }

  return `${toDisplayDate(startValue)} ${toDisplayTime(startValue)} - ${toDisplayDate(endValue)} ${toDisplayTime(endValue)}`;
};

const getAudienceLabel = (session: ScheduleSessionItem): string => {
  if ((session.targetType || '').toLowerCase() === 'students') {
    return session.studentDisplayName || (session.studentUserId ? `Student ${session.studentUserId}` : 'Student');
  }

  return session.classroomName || (session.classroomId ? `Classroom ${session.classroomId}` : 'Classroom');
};

const getStatusChipStyle = (status: SessionStatus): React.CSSProperties => {
  if (status === 'Completed') {
    return { border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#166534' };
  }

  if (status === 'Cancelled') {
    return { border: '1px solid #fecaca', background: '#fff1f2', color: '#b91c1c' };
  }

  if (status === 'Rescheduled') {
    return { border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8' };
  }

  return { border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155' };
};

const toEditState = (session: ScheduleSessionItem): SessionEditFormState => ({
  startDateTime: toDateTimeInput(session.startDateTime),
  endDateTime: toDateTimeInput(session.endDateTime),
  status: session.status,
  location: session.location || '',
  meetingUrl: session.meetingUrl || '',
  notes: session.notes || '',
});

const ScheduleSessionsPanel: React.FC<ScheduleSessionsPanelProps> = ({
  sessions,
  loading = false,
  mode = 'tab',
  onUpdateSession,
  onStartSession,
  onCompleteSession,
  onRecordAttendance,
}) => {
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<SessionEditFormState | null>(null);
  const [savingSessionId, setSavingSessionId] = useState<number | null>(null);
  const [attendanceDraft, setAttendanceDraft] = useState<Record<number, {
    userId: string;
    status: AttendanceStatus;
    joinedAt: string;
    leftAt: string;
    notes: string;
  }>>({});
  const [attendanceSavingSessionId, setAttendanceSavingSessionId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sortedSessions = useMemo(() => [...sessions].sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()), [sessions]);

  const beginEdit = (session: ScheduleSessionItem) => {
    setEditingSessionId(session.id);
    setEditForm(toEditState(session));
    setError(null);
  };

  const cancelEdit = () => {
    setEditingSessionId(null);
    setEditForm(null);
    setError(null);
  };

  const saveEdit = async (sessionId: number) => {
    if (!editForm || !onUpdateSession) {
      return;
    }

    const start = new Date(editForm.startDateTime);
    const end = new Date(editForm.endDateTime);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setError('Please provide valid start and end date-time values.');
      return;
    }

    if (end <= start) {
      setError('End date-time must be after start date-time.');
      return;
    }

    try {
      setSavingSessionId(sessionId);
      setError(null);

      await onUpdateSession(sessionId, {
        startDateTime: start.toISOString(),
        endDateTime: end.toISOString(),
        status: editForm.status,
        location: editForm.location.trim() || null,
        meetingUrl: editForm.meetingUrl.trim() || null,
        notes: editForm.notes.trim() || null,
      });

      cancelEdit();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update session.');
    } finally {
      setSavingSessionId(null);
    }
  };

  const updateAttendanceDraft = (sessionId: number, patch: Partial<{ userId: string; status: AttendanceStatus; joinedAt: string; leftAt: string; notes: string }>) => {
    setAttendanceDraft((previous) => ({
      ...previous,
      [sessionId]: {
        userId: previous[sessionId]?.userId ?? '',
        status: previous[sessionId]?.status ?? 'Present',
        joinedAt: previous[sessionId]?.joinedAt ?? '',
        leftAt: previous[sessionId]?.leftAt ?? '',
        notes: previous[sessionId]?.notes ?? '',
        ...patch,
      },
    }));
  };

  const handleRecordAttendance = async (sessionId: number) => {
    if (!onRecordAttendance) {
      return;
    }

    const draft = attendanceDraft[sessionId] ?? { userId: '', status: 'Present', joinedAt: '', leftAt: '', notes: '' };
    const userId = Number(draft.userId);

    if (!Number.isInteger(userId) || userId <= 0) {
      setError('Please enter a valid user ID for attendance.');
      return;
    }

    try {
      setAttendanceSavingSessionId(sessionId);
      setError(null);
      await onRecordAttendance(sessionId, {
        userId,
        status: draft.status,
        joinedAt: draft.joinedAt ? new Date(draft.joinedAt).toISOString() : null,
        leftAt: draft.leftAt ? new Date(draft.leftAt).toISOString() : null,
        notes: draft.notes.trim() || null,
      });
      setAttendanceDraft((previous) => ({
        ...previous,
        [sessionId]: { userId: '', status: 'Present', joinedAt: '', leftAt: '', notes: '' },
      }));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to record attendance.');
    } finally {
      setAttendanceSavingSessionId(null);
    }
  };

  const panelPadding = mode === 'popup' ? 12 : 16;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {error && (
        <div style={{ borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#b91c1c', padding: '10px 12px', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: panelPadding, display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#173159' }}>Individual Sessions</h3>
          <div style={{ fontSize: 12, color: '#64748b' }}>{sortedSessions.length} total</div>
        </div>

        {loading ? (
          <div style={{ color: '#475569', padding: 8 }}>Loading sessions...</div>
        ) : sortedSessions.length === 0 ? (
          <div style={{ color: '#64748b', padding: 8 }}>No sessions available for this schedule.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {sortedSessions.map((session) => {
              const isEditing = editingSessionId === session.id;
              const isSaving = savingSessionId === session.id;

              return (
                <div key={session.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 10, background: isEditing ? '#f8fbff' : '#fff' }}>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <div style={{ color: '#0f172a', fontWeight: 600 }}>{formatSessionRange(session.startDateTime, session.endDateTime)}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          Audience: {getAudienceLabel(session)} | Teacher: {session.teacherDisplayName || `User ${session.teacherUserId}`}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700, ...getStatusChipStyle(session.status) }}>
                          {session.status}
                        </span>
                        {session.isModified && (
                          <span style={{ borderRadius: 999, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 11, padding: '2px 8px', fontWeight: 600 }}>
                            Modified
                          </span>
                        )}
                        {onUpdateSession && (
                          <button type="button" className="amd-btn amd-btn-ghost" onClick={() => beginEdit(session)} disabled={isSaving}>
                            Edit
                          </button>
                        )}
                      </div>
                    </div>

                    {!isEditing && (
                      <div style={{ display: 'grid', gap: 4, fontSize: 13, color: '#334155' }}>
                        <div><strong>Location:</strong> {session.location || '—'}</div>
                        <div><strong>Meeting URL:</strong> {session.meetingUrl || '—'}</div>
                        <div><strong>Actual Start:</strong> {session.actualStartDateTime ? toDisplayDateTime(session.actualStartDateTime) : '—'}</div>
                        <div><strong>Actual End:</strong> {session.actualEndDateTime ? toDisplayDateTime(session.actualEndDateTime) : '—'}</div>
                        <div><strong>Notes:</strong> {session.notes || '—'}</div>
                      </div>
                    )}

                    {!isEditing && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                        {onStartSession && session.status !== 'InProgress' && session.status !== 'Completed' && (
                          <button type="button" className="amd-btn amd-btn-primary" onClick={() => { void onStartSession(session.id); }}>
                            Start Session
                          </button>
                        )}
                        {onCompleteSession && session.status !== 'Completed' && (
                          <button type="button" className="amd-btn amd-btn-secondary" onClick={() => { void onCompleteSession(session.id); }}>
                            Complete Session
                          </button>
                        )}
                      </div>
                    )}

                    {!isEditing && onRecordAttendance && (
                      <div style={{ marginTop: 12, borderTop: '1px solid #e2e8f0', paddingTop: 10, display: 'grid', gap: 8 }}>
                        <div style={{ fontWeight: 600, color: '#173159' }}>Attendance</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>User ID</span>
                            <input
                              type="number"
                              min={1}
                              value={attendanceDraft[session.id]?.userId ?? ''}
                              onChange={(event) => updateAttendanceDraft(session.id, { userId: event.target.value })}
                            />
                          </label>

                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>Status</span>
                            <select
                              value={attendanceDraft[session.id]?.status ?? 'Present'}
                              onChange={(event) => updateAttendanceDraft(session.id, { status: event.target.value as AttendanceStatus })}
                            >
                              {ATTENDANCE_STATUS_OPTIONS.map((statusOption) => (
                                <option key={statusOption} value={statusOption}>{statusOption}</option>
                              ))}
                            </select>
                          </label>

                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>Joined</span>
                            <input
                              type="datetime-local"
                              value={attendanceDraft[session.id]?.joinedAt ?? ''}
                              onChange={(event) => updateAttendanceDraft(session.id, { joinedAt: event.target.value })}
                            />
                          </label>

                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>Left</span>
                            <input
                              type="datetime-local"
                              value={attendanceDraft[session.id]?.leftAt ?? ''}
                              onChange={(event) => updateAttendanceDraft(session.id, { leftAt: event.target.value })}
                            />
                          </label>
                        </div>

                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: '#475569' }}>Notes</span>
                          <input
                            type="text"
                            value={attendanceDraft[session.id]?.notes ?? ''}
                            onChange={(event) => updateAttendanceDraft(session.id, { notes: event.target.value })}
                          />
                        </label>

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            className="amd-btn amd-btn-primary"
                            onClick={() => { void handleRecordAttendance(session.id); }}
                            disabled={attendanceSavingSessionId === session.id}
                          >
                            {attendanceSavingSessionId === session.id ? 'Saving...' : 'Record Attendance'}
                          </button>
                        </div>
                      </div>
                    )}

                    {isEditing && editForm && (
                      <div style={{ display: 'grid', gap: 10, paddingTop: 8, borderTop: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>Start</span>
                            <input
                              type="datetime-local"
                              value={editForm.startDateTime}
                              onChange={(event) => setEditForm((previous) => previous ? { ...previous, startDateTime: event.target.value } : previous)}
                            />
                          </label>

                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>End</span>
                            <input
                              type="datetime-local"
                              value={editForm.endDateTime}
                              onChange={(event) => setEditForm((previous) => previous ? { ...previous, endDateTime: event.target.value } : previous)}
                            />
                          </label>

                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>Status</span>
                            <select
                              value={editForm.status}
                              onChange={(event) => setEditForm((previous) => previous ? { ...previous, status: event.target.value as SessionStatus } : previous)}
                            >
                              {SESSION_STATUS_OPTIONS.map((statusOption) => (
                                <option key={statusOption} value={statusOption}>{statusOption}</option>
                              ))}
                            </select>
                          </label>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>Location</span>
                            <input
                              type="text"
                              value={editForm.location}
                              onChange={(event) => setEditForm((previous) => previous ? { ...previous, location: event.target.value } : previous)}
                            />
                          </label>

                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>Meeting URL</span>
                            <input
                              type="url"
                              value={editForm.meetingUrl}
                              onChange={(event) => setEditForm((previous) => previous ? { ...previous, meetingUrl: event.target.value } : previous)}
                            />
                          </label>
                        </div>

                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: '#475569' }}>Notes</span>
                          <textarea
                            rows={2}
                            value={editForm.notes}
                            onChange={(event) => setEditForm((previous) => previous ? { ...previous, notes: event.target.value } : previous)}
                          />
                        </label>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <button type="button" className="amd-btn amd-btn-ghost" onClick={cancelEdit} disabled={isSaving}>Cancel</button>
                          <button type="button" className="amd-btn amd-btn-primary" onClick={() => { void saveEdit(session.id); }} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Update Session'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleSessionsPanel;
